import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

const CONTACT_EMAIL = "yayadentalclinic@gmail.com";

// Shared nav items so the header and footer quick links always stay in sync.
const NAV_ITEMS = [
  { label: "Home", to: "/" },
  { label: "Our Services", to: "/services" },
  { label: "Our Team", to: "/our-team" },
];

const BOOK_HREF = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
  "Appointment request"
)}`;

const CSS = `
@import url("https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600&family=Young+Serif&display=swap");

.nb {
  --nb-ink: #10303a;
  --nb-body: #3f5a62;
  --nb-teal: #0d7a85;
  --nb-teal-dark: #0a5f68;
  --nb-mist: #eaf4f3;
  --nb-line: #d3e5e4;
  --nb-h: 4rem;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1040;
  font-family: "Figtree", system-ui, -apple-system, "Segoe UI", sans-serif;
  background: rgba(255, 255, 255, 0.92);
  -webkit-backdrop-filter: saturate(160%) blur(12px);
  backdrop-filter: saturate(160%) blur(12px);
  border-bottom: 1px solid var(--nb-line);
  padding-top: env(safe-area-inset-top, 0px);
}
@media (min-width: 62rem) { .nb { --nb-h: 4.5rem; } }
.nb *, .nb *::before, .nb *::after { box-sizing: border-box; }

.nb-bar {
  max-width: 72rem;
  margin: 0 auto;
  height: var(--nb-h);
  padding: 0 clamp(1rem, 4vw, 2.5rem);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

/* Brand */
.nb-brand {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
  color: var(--nb-ink);
  text-decoration: none;
  font-family: "Young Serif", Georgia, serif;
  font-size: clamp(1.05rem, 0.9rem + 0.7vw, 1.35rem);
  line-height: 1.1;
  white-space: nowrap;
}
.nb-brand-icon {
  flex: none;
  width: 2.4rem;
  height: 2.4rem;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: var(--nb-teal);
  color: #fff;
  font-size: 1rem;
}
@media (max-width: 22rem) { .nb-brand span:last-child { font-size: 0.98rem; } }

/* Toggle */
.nb-toggle {
  flex: none;
  width: 2.75rem;
  height: 2.75rem;
  display: grid;
  place-items: center;
  border: 1.5px solid var(--nb-line);
  border-radius: 50%;
  background: #fff;
  color: var(--nb-ink);
  font-size: 1.1rem;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
}
.nb-toggle:hover { background: var(--nb-mist); border-color: var(--nb-teal); }
@media (min-width: 62rem) { .nb-toggle { display: none; } }

/* Menu: dropdown panel on mobile, inline row on desktop */
.nb-panel {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.25s ease;
}
.nb-panel.is-open { grid-template-rows: 1fr; }
.nb-panel-inner { overflow: hidden; min-height: 0; }
.nb-menu {
  list-style: none;
  margin: 0;
  padding: 0.5rem clamp(1rem, 4vw, 2.5rem) 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-width: 72rem;
  margin: 0 auto;
}
.nb-link {
  display: flex;
  align-items: center;
  min-height: 3rem;
  padding: 0 1rem;
  border-radius: 0.85rem;
  color: var(--nb-body);
  font-weight: 500;
  font-size: 1.05rem;
  text-decoration: none;
  transition: background 0.2s ease, color 0.2s ease;
}
.nb-link:hover { background: var(--nb-mist); color: var(--nb-ink); }
.nb-link.is-active { background: var(--nb-mist); color: var(--nb-teal-dark); font-weight: 600; }

.nb-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  min-height: 3rem;
  margin-top: 0.5rem;
  padding: 0.7rem 1.4rem;
  border-radius: 999px;
  background: var(--nb-teal);
  color: #fff;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.2s ease;
}
.nb-cta:hover { background: var(--nb-teal-dark); color: #fff; }

.nb :is(a, button):focus-visible { outline: 3px solid #f0b429; outline-offset: 3px; }

@media (min-width: 62rem) {
  .nb { display: block; }
  .nb-bar { position: relative; }
  .nb-panel {
    display: block;
    transition: none;
    margin-left: auto;
  }
  .nb-panel-inner { overflow: visible; }
  .nb-menu {
    flex-direction: row;
    align-items: center;
    gap: 0.25rem;
    padding: 0;
    margin: 0;
    max-width: none;
  }
  .nb-link { min-height: 2.75rem; padding: 0 1.1rem; border-radius: 999px; font-size: 1rem; }
  .nb-cta { margin: 0 0 0 0.75rem; }
}

/* On desktop the toggle is hidden and the panel sits inside the bar row.
   On mobile the panel is moved below the bar by being a sibling of it. */
.nb-desktop-slot { display: none; }
@media (min-width: 62rem) { .nb-desktop-slot { display: block; } .nb-mobile-slot { display: none; } }

@media (prefers-reduced-motion: reduce) {
  .nb-panel, .nb-toggle, .nb-link, .nb-cta { transition: none; }
}
`;

function Menu({ onNavigate, tabbable = true }) {
  const tab = tabbable ? undefined : -1;
  return (
    <ul className="nb-menu">
      {NAV_ITEMS.map((item) => (
        <li key={item.to}>
          <NavLink
            className={({ isActive }) => `nb-link${isActive ? " is-active" : ""}`}
            end={item.to === "/"}
            to={item.to}
            onClick={onNavigate}
            tabIndex={tab}
          >
            {item.label}
          </NavLink>
        </li>
      ))}
      <li>
        <a className="nb-cta" href={BOOK_HREF} onClick={onNavigate} tabIndex={tab}>
          <i className="fa-regular fa-calendar-check" aria-hidden="true"></i>
          Book appointment
        </a>
      </li>
    </ul>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const location = useLocation();

  // Close the mobile menu after navigating.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Close on Escape, on outside click, and when the viewport reaches desktop width.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const onPointer = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const mq = window.matchMedia("(min-width: 62rem)");
    const onChange = (e) => e.matches && setOpen(false);

    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    mq.addEventListener("change", onChange);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
      mq.removeEventListener("change", onChange);
    };
  }, [open]);

  return (
    <header className="nb" ref={rootRef}>
      <style>{CSS}</style>

      <div className="nb-bar">
        <Link className="nb-brand" to="/" aria-label="Yaya Dental Clinic home">
          <span className="nb-brand-icon" aria-hidden="true">
            <i className="fa-solid fa-tooth"></i>
          </span>
          <span>Yaya Dental Clinic</span>
        </Link>

        {/* Desktop: links live inside the bar */}
        <nav className="nb-desktop-slot" aria-label="Main">
          <div className="nb-panel">
            <div className="nb-panel-inner">
              <Menu />
            </div>
          </div>
        </nav>

        <button
          type="button"
          className="nb-toggle"
          aria-expanded={open}
          aria-controls="nb-mobile-menu"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
        >
          <i className={`fa-solid ${open ? "fa-xmark" : "fa-bars"}`} aria-hidden="true"></i>
        </button>
      </div>

      {/* Mobile and tablet: dropdown below the bar */}
      <nav className="nb-mobile-slot" aria-label="Main mobile">
        <div
          id="nb-mobile-menu"
          className={`nb-panel${open ? " is-open" : ""}`}
          aria-hidden={!open}
        >
          <div className="nb-panel-inner">
            <Menu onNavigate={() => setOpen(false)} tabbable={open} />
          </div>
        </div>
      </nav>
    </header>
  );
}